import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Plus, X, Trash2, Edit, HelpCircle, Search, Check,
    Filter, List, ArrowLeft, Info, Database, FileText
} from "lucide-react";
import {
    getQuizRequest,
    getQuestionsByQuizRequest,
    createQuestionRequest,
    updateQuestionRequest,
    deleteQuestionRequest,
    clearQuizMessage
} from '../../redux/actions/quizActions';
import {
    fetchDomainesRequest,
    fetchThemesRequest
} from '../../redux/actions/paramDomainesThemesActions';
import './QuizManagement.css';

const QUESTION_TYPE_OPTIONS = [
    { value: 'QCU', label: 'QCU' },
    { value: 'QCM', label: 'QCM' },
    { value: 'OUI_NON', label: 'Oui / Non' },
    { value: 'DISSERTATION', label: 'Dissertation' },
    { value: 'MATCHING', label: 'Matching / Correspondance' },
    { value: 'CLASSEMENT', label: 'Classement' },
    { value: 'GAMING', label: 'Gamification' },
    { value: 'COMPLETEMENT', label: 'Texte à compléter' }
];

const ANIMATION_OPTIONS = [
    { value: 'confetti', label: 'Confetti' },
    { value: 'bounce', label: 'Bounce' },
    { value: 'flash', label: 'Flash' },
    { value: 'none', label: 'Aucune' }
];

const emptyAnswer = {
    libelle: '',
    estCorrecte: false,
    ordreCorrect: '',
    paireId: ''
};

const emptyMatchingPair = {
    left: '',
    right: ''
};

const emptyGridItem = {
    id: '',
    label: '',
    max: ''
};

const buildInitialAdvancedConfig = (editingQuestion = null, typeQuestion = 'QCU') => {
    const config = editingQuestion?.configJson || null;

    if (typeQuestion === 'MATCHING') {
        const left = config?.col_gauche || [];
        const right = config?.col_droite || [];
        const pairs = [];

        const max = Math.max(left.length, right.length);
        for (let i = 0; i < max; i++) {
            pairs.push({
                left: left[i] || '',
                right: right[i] || ''
            });
        }

        return {
            matchingPairs: pairs.length ? pairs : [{ ...emptyMatchingPair }, { ...emptyMatchingPair }],
            dissertationConfig: {
                nbMotsMin: '',
                nbMotsMax: '',
                grille: [{ ...emptyGridItem }]
            },
            gamingConfig: {
                timer_s: 15,
                animation: 'confetti'
            },
            completionConfig: {
                expected: ''
            }
        };
    }

    if (typeQuestion === 'DISSERTATION') {
        return {
            matchingPairs: [{ ...emptyMatchingPair }, { ...emptyMatchingPair }],
            dissertationConfig: {
                nbMotsMin: config?.nb_mots_min ?? '',
                nbMotsMax: config?.nb_mots_max ?? 300,
                grille: config?.grille?.length
                    ? config.grille.map((item) => ({
                        id: item.id || '',
                        label: item.label || '',
                        max: item.max ?? ''
                    }))
                    : [{ ...emptyGridItem }]
            },
            gamingConfig: {
                timer_s: 15,
                animation: 'confetti'
            },
            completionConfig: {
                expected: ''
            }
        };
    }

    if (typeQuestion === 'GAMING') {
        return {
            matchingPairs: [{ ...emptyMatchingPair }, { ...emptyMatchingPair }],
            dissertationConfig: {
                nbMotsMin: '',
                nbMotsMax: '',
                grille: [{ ...emptyGridItem }]
            },
            gamingConfig: {
                timer_s: config?.timer_s ?? 15,
                animation: config?.animation || 'confetti'
            },
            completionConfig: {
                expected: ''
            }
        };
    }

    if (typeQuestion === 'COMPLETEMENT') {
        return {
            matchingPairs: [{ ...emptyMatchingPair }, { ...emptyMatchingPair }],
            dissertationConfig: {
                nbMotsMin: '',
                nbMotsMax: '',
                grille: [{ ...emptyGridItem }]
            },
            gamingConfig: {
                timer_s: 15,
                animation: 'confetti'
            },
            completionConfig: {
                expected: config?.expected || ''
            }
        };
    }

    return {
        matchingPairs: [{ ...emptyMatchingPair }, { ...emptyMatchingPair }],
        dissertationConfig: {
            nbMotsMin: '',
            nbMotsMax: '',
            grille: [{ ...emptyGridItem }]
        },
        gamingConfig: {
            timer_s: 15,
            animation: 'confetti'
        },
        completionConfig: {
            expected: ''
        }
    };
};

const buildYesNoAnswers = () => ([
    { libelle: 'Oui', estCorrecte: false, ordreCorrect: '', paireId: '' },
    { libelle: 'Non', estCorrecte: false, ordreCorrect: '', paireId: '' }
]);

const QuestionModal = ({
    isOpen,
    onClose,
    onSubmit,
    editingQuestion,
    loading,
    quizId,
    domaines,
    themes,
    nextOrder = 1
}) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        quizId,
        domaineId: '',
        themeId: '',
        enonce: '',
        typeQuestion: 'QCU',
        points: 1,
        ordre: nextOrder,
        mediaUrl: '',
        explicationCorrection: '',
        dureeQuestionS: '',
        reponsesPossibles: [{ ...emptyAnswer }, { ...emptyAnswer }]
    });

    const [matchingPairs, setMatchingPairs] = useState([{ ...emptyMatchingPair }, { ...emptyMatchingPair }]);
    const [dissertationConfig, setDissertationConfig] = useState({
        nbMotsMin: '',
        nbMotsMax: 300,
        grille: [{ ...emptyGridItem }]
    });
    const [gamingConfig, setGamingConfig] = useState({
        timer_s: 15,
        animation: 'confetti'
    });
    const [completionConfig, setCompletionConfig] = useState({
        expected: ''
    });

    useEffect(() => {
        if (editingQuestion) {
            const type = editingQuestion.typeQuestion || 'QCU';
            const advanced = buildInitialAdvancedConfig(editingQuestion, type);

            setFormData({
                quizId,
                domaineId: editingQuestion.domaineId || '',
                themeId: editingQuestion.themeId || '',
                enonce: editingQuestion.enonce || '',
                typeQuestion: type,
                points: editingQuestion.points ?? 1,
                ordre: editingQuestion.ordre ?? 1,
                mediaUrl: editingQuestion.mediaUrl || '',
                explicationCorrection: editingQuestion.explicationCorrection || '',
                dureeQuestionS: editingQuestion.dureeQuestionS ?? '',
                reponsesPossibles: editingQuestion.reponsesPossibles?.length
                    ? editingQuestion.reponsesPossibles.map((rp) => ({
                        id: rp.id,
                        libelle: rp.libelle || '',
                        estCorrecte: !!rp.estCorrecte,
                        ordreCorrect: rp.ordreCorrect ?? '',
                        paireId: rp.paireId || ''
                    }))
                    : (
                        type === 'OUI_NON'
                            ? buildYesNoAnswers()
                            : [{ ...emptyAnswer }, { ...emptyAnswer }]
                    )
            });

            setMatchingPairs(advanced.matchingPairs);
            setDissertationConfig(advanced.dissertationConfig);
            setGamingConfig(advanced.gamingConfig);
            setCompletionConfig(advanced.completionConfig);
        } else {
            setFormData({
                quizId,
                domaineId: '',
                themeId: '',
                enonce: '',
                typeQuestion: 'QCU',
                points: 1,
                ordre: nextOrder,
                mediaUrl: '',
                explicationCorrection: '',
                dureeQuestionS: '',
                reponsesPossibles: [{ ...emptyAnswer }, { ...emptyAnswer }]
            });

            const advanced = buildInitialAdvancedConfig(null, 'QCU');
            setMatchingPairs(advanced.matchingPairs);
            setDissertationConfig(advanced.dissertationConfig);
            setGamingConfig(advanced.gamingConfig);
            setCompletionConfig(advanced.completionConfig);
        }
    }, [editingQuestion, isOpen, nextOrder, quizId]);

    if (!isOpen) return null;

    const filteredThemes = themes.filter((theme) => {
        if (!formData.domaineId) return true;
        const themeDomaineId = theme.domaineId || theme.domainId || theme.domaine?.id || theme.domaine?.reference;
        return String(themeDomaineId) === String(formData.domaineId);
    });

    const type = formData.typeQuestion;
    const isDissertation = type === 'DISSERTATION';
    const isMatching = type === 'MATCHING';
    const isClassement = type === 'CLASSEMENT';
    const isGaming = type === 'GAMING';
    const isCompletion = type === 'COMPLETEMENT';
    const isYesNo = type === 'OUI_NON';
    const requiresAnswers = !isDissertation && !isCompletion;

    const handleField = (name, value) => {
        if (name === 'typeQuestion') {
            const advanced = buildInitialAdvancedConfig(null, value);

            let defaultAnswers = [{ ...emptyAnswer }, { ...emptyAnswer }];

            if (value === 'OUI_NON') {
                defaultAnswers = buildYesNoAnswers();
            } else if (value === 'DISSERTATION' || value === 'COMPLETEMENT') {
                defaultAnswers = [];
            }

            setFormData((prev) => ({
                ...prev,
                typeQuestion: value,
                dureeQuestionS: value === 'GAMING' ? (prev.dureeQuestionS || 15) : '',
                reponsesPossibles: defaultAnswers
            }));

            setMatchingPairs(advanced.matchingPairs);
            setDissertationConfig(advanced.dissertationConfig);
            setGamingConfig(advanced.gamingConfig);
            setCompletionConfig(advanced.completionConfig);
            return;
        }

        if (name === 'domaineId') {
            setFormData((prev) => ({
                ...prev,
                domaineId: value,
                themeId: ''
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAnswerChange = (index, field, value) => {
        setFormData((prev) => {
            const reponsesPossibles = [...prev.reponsesPossibles];
            reponsesPossibles[index] = { ...reponsesPossibles[index], [field]: value };
            return { ...prev, reponsesPossibles };
        });
    };

    const addAnswerRow = () => {
        setFormData((prev) => ({
            ...prev,
            reponsesPossibles: [...prev.reponsesPossibles, { ...emptyAnswer }]
        }));
    };

    const removeAnswerRow = (index) => {
        setFormData((prev) => ({
            ...prev,
            reponsesPossibles: prev.reponsesPossibles.filter((_, i) => i !== index)
        }));
    };

    const handleMatchingPairChange = (index, field, value) => {
        setMatchingPairs((prev) => {
            const pairs = [...prev];
            pairs[index] = { ...pairs[index], [field]: value };
            return pairs;
        });
    };

    const addMatchingPair = () => {
        setMatchingPairs((prev) => [...prev, { ...emptyMatchingPair }]);
    };

    const removeMatchingPair = (index) => {
        setMatchingPairs((prev) => prev.filter((_, i) => i !== index));
    };

    const handleGrilleChange = (index, field, value) => {
        setDissertationConfig((prev) => {
            const grille = [...prev.grille];
            grille[index] = { ...grille[index], [field]: value };
            return { ...prev, grille };
        });
    };

    const addGrilleItem = () => {
        setDissertationConfig((prev) => ({
            ...prev,
            grille: [...prev.grille, { ...emptyGridItem }]
        }));
    };

    const removeGrilleItem = (index) => {
        setDissertationConfig((prev) => ({
            ...prev,
            grille: prev.grille.filter((_, i) => i !== index)
        }));
    };

    const buildPayload = () => {
        let configJson = null;
        let reponsesPossibles = [];

        if (isMatching) {
            const validPairs = matchingPairs.filter((pair) => pair.left?.trim() && pair.right?.trim());

            configJson = {
                col_gauche: validPairs.map((pair) => pair.left),
                col_droite: validPairs.map((pair) => pair.right)
            };

            reponsesPossibles = validPairs.flatMap((pair, index) => {
                const pairId = `PAIR_${index + 1}`;
                return [
                    {
                        libelle: pair.left,
                        estCorrecte: true,
                        ordreCorrect: null,
                        paireId: pairId
                    },
                    {
                        libelle: pair.right,
                        estCorrecte: true,
                        ordreCorrect: null,
                        paireId: pairId
                    }
                ];
            });
        } else if (isDissertation) {
            configJson = {
                correction: 'manuelle',
                nb_mots_min: dissertationConfig.nbMotsMin !== '' ? Number(dissertationConfig.nbMotsMin) : 0,
                nb_mots_max: dissertationConfig.nbMotsMax !== '' ? Number(dissertationConfig.nbMotsMax) : 300,
                grille: dissertationConfig.grille
                    .filter((item) => item.label?.trim())
                    .map((item, index) => ({
                        id: item.id?.trim() || `c${index + 1}`,
                        label: item.label,
                        max: item.max !== '' ? Number(item.max) : 0
                    }))
            };

            reponsesPossibles = [];
        } else if (isGaming) {
            configJson = {
                timer_s: gamingConfig.timer_s !== '' ? Number(gamingConfig.timer_s) : 15,
                animation: gamingConfig.animation || 'confetti'
            };

            reponsesPossibles = formData.reponsesPossibles
                .filter((rp) => rp.libelle?.trim())
                .map((rp) => ({
                    id: rp.id,
                    libelle: rp.libelle,
                    estCorrecte: !!rp.estCorrecte,
                    ordreCorrect: null,
                    paireId: null
                }));
        } else if (isCompletion) {
            configJson = {
                expected: completionConfig.expected || ''
            };

            reponsesPossibles = [];
        } else if (isClassement) {
            configJson = null;

            reponsesPossibles = formData.reponsesPossibles
                .filter((rp) => rp.libelle?.trim())
                .map((rp) => ({
                    id: rp.id,
                    libelle: rp.libelle,
                    estCorrecte: true,
                    ordreCorrect: rp.ordreCorrect !== '' ? Number(rp.ordreCorrect) : null,
                    paireId: null
                }));
        } else {
            configJson = null;

            reponsesPossibles = formData.reponsesPossibles
                .filter((rp) => rp.libelle?.trim())
                .map((rp) => ({
                    id: rp.id,
                    libelle: rp.libelle,
                    estCorrecte: !!rp.estCorrecte,
                    ordreCorrect: null,
                    paireId: null
                }));
        }

        return {
            quizId: quizId,
            domaineId: formData.domaineId ? Number(formData.domaineId) : null,
            themeId: formData.themeId ? Number(formData.themeId) : null,
            enonce: formData.enonce,
            typeQuestion: formData.typeQuestion,
            configJson,
            points: Number(formData.points),
            ordre: Number(formData.ordre),
            mediaUrl: formData.mediaUrl || null,
            explicationCorrection: formData.explicationCorrection || null,
            dureeQuestionS: isGaming
                ? (gamingConfig.timer_s !== '' ? Number(gamingConfig.timer_s) : 15)
                : (formData.dureeQuestionS !== '' ? Number(formData.dureeQuestionS) : null),
            reponsesPossibles
        };
    };

    const validateBeforeSubmit = (payload) => {
        if (!payload.quizId) {
            Swal.fire({ icon: 'error', text: t('Quiz ID obligatoire'), confirmButtonText: 'OK' });
            return false;
        }

        if (!payload.enonce?.trim()) {
            Swal.fire({ icon: 'error', text: t("L'énoncé est obligatoire"), confirmButtonText: 'OK' });
            return false;
        }

        if (isMatching) {
            if (!matchingPairs.filter((p) => p.left?.trim() && p.right?.trim()).length) {
                Swal.fire({ icon: 'error', text: t('Ajoute au moins une paire de correspondance'), confirmButtonText: 'OK' });
                return false;
            }
        }

        if (isDissertation) {
            if (!dissertationConfig.grille.filter((g) => g.label?.trim()).length) {
                Swal.fire({ icon: 'error', text: t('Ajoute au moins un critère de correction'), confirmButtonText: 'OK' });
                return false;
            }
        }

        if (requiresAnswers && !payload.reponsesPossibles.length) {
            Swal.fire({ icon: 'error', text: t('Ajoute au moins une réponse'), confirmButtonText: 'OK' });
            return false;
        }

        if ((type === 'QCU' || type === 'OUI_NON' || type === 'GAMING') &&
            !payload.reponsesPossibles.some((rp) => rp.estCorrecte)) {
            Swal.fire({ icon: 'error', text: t('Sélectionne au moins une bonne réponse'), confirmButtonText: 'OK' });
            return false;
        }

        if (type === 'CLASSEMENT' &&
            payload.reponsesPossibles.some((rp) => rp.ordreCorrect == null)) {
            Swal.fire({ icon: 'error', text: t('Renseigne ordre correct pour chaque réponse'), confirmButtonText: 'OK' });
            return false;
        }

        if (type === 'COMPLETEMENT' && !completionConfig.expected?.trim()) {
            Swal.fire({ icon: 'error', text: t('La réponse attendue est obligatoire'), confirmButtonText: 'OK' });
            return false;
        }

        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = buildPayload();

        if (!validateBeforeSubmit(payload)) return;

        onSubmit(payload);
    };

    return (
        <div className="qz-modal-overlay" onClick={onClose}>
            <div className="qz-modal" style={{ width: 'min(980px, 100%)' }} onClick={(e) => e.stopPropagation()}>
                <div className="qz-modal-header">
                    <h3>
                        <HelpCircle size={16} style={{ color: "#dc3545" }} />
                        {editingQuestion ? t('Modifier la question') : t('Ajouter une question')}
                    </h3>
                    <button className="qz-x" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="qz-modal-body">
                    <form id="question-form" onSubmit={handleSubmit}>
                        <div className="qz-grid">
                            <div className="qz-field qz-field--full">
                                <label>{t('Énoncé')} *</label>
                                <textarea
                                    rows="3"
                                    value={formData.enonce}
                                    onChange={(e) => handleField('enonce', e.target.value)}
                                    placeholder={t('Saisissez l\'énoncé de la question...')}
                                    required
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Type de question')} *</label>
                                <select
                                    value={formData.typeQuestion}
                                    onChange={(e) => handleField('typeQuestion', e.target.value)}
                                >
                                    {QUESTION_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="qz-field">
                                <label>{t('Barème')} *</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.points}
                                    onChange={(e) => handleField('points', e.target.value)}
                                    required
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Ordre')} *</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={formData.ordre}
                                    onChange={(e) => handleField('ordre', e.target.value)}
                                    required
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Durée question (sec)')}</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={isGaming ? gamingConfig.timer_s : formData.dureeQuestionS}
                                    onChange={(e) => {
                                        if (isGaming) {
                                            setGamingConfig((prev) => ({ ...prev, timer_s: e.target.value }));
                                        } else {
                                            handleField('dureeQuestionS', e.target.value);
                                        }
                                    }}
                                    placeholder={isGaming ? "Requis pour Gaming" : "Optionnel"}
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Domaine')}</label>
                                <select
                                    value={formData.domaineId}
                                    onChange={(e) => handleField('domaineId', e.target.value)}
                                >
                                    <option value="">{t('Sélectionner...')}</option>
                                    {domaines.map((domaine) => (
                                        <option key={domaine.id} value={domaine.id}>
                                            {domaine.nom || domaine.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="qz-field">
                                <label>{t('Thème')}</label>
                                <select
                                    value={formData.themeId}
                                    onChange={(e) => handleField('themeId', e.target.value)}
                                >
                                    <option value="">{t('Sélectionner...')}</option>
                                    {filteredThemes.map((theme) => (
                                        <option key={theme.id} value={theme.id}>
                                            {theme.nom || theme.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="qz-field">
                                <label>{t('Media URL')}</label>
                                <input
                                    value={formData.mediaUrl}
                                    onChange={(e) => handleField('mediaUrl', e.target.value)}
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="qz-field">
                                <label>{t('Explication correction')}</label>
                                <input
                                    value={formData.explicationCorrection}
                                    onChange={(e) => handleField('explicationCorrection', e.target.value)}
                                    placeholder={t('Affiché après réponse')}
                                />
                            </div>
                        </div>

                        <div className="qz-divider" />

                        {(type === 'QCU' || type === 'QCM' || type === 'OUI_NON') && (
                            <div className="qz-section">
                                <div className="qz-section-head">
                                    <h4>{t('Réponses possibles')}</h4>
                                    {!isYesNo && (
                                        <button type="button" className="qz-btn qz-btn-light" onClick={addAnswerRow}>
                                            <Plus size={14} /> {t('Ajouter')}
                                        </button>
                                    )}
                                </div>

                                {formData.reponsesPossibles.map((answer, index) => (
                                    <div key={index} className="qz-skill-row" style={{ gridTemplateColumns: '1fr auto 40px' }}>
                                        <input
                                            value={answer.libelle}
                                            onChange={(e) => handleAnswerChange(index, 'libelle', e.target.value)}
                                            placeholder={t('Libellé de la réponse')}
                                            disabled={isYesNo}
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#2f4595', cursor: 'pointer' }}>
                                            <input
                                                type={type === 'QCU' || type === 'OUI_NON' ? 'radio' : 'checkbox'}
                                                name="correct-answer"
                                                checked={!!answer.estCorrecte}
                                                style={{ width: 14, height: 14, margin: 0 }}
                                                onChange={(e) => {
                                                    if (type === 'QCU' || type === 'OUI_NON') {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            reponsesPossibles: prev.reponsesPossibles.map((rp, i) => ({
                                                                ...rp,
                                                                estCorrecte: i === index
                                                            }))
                                                        }));
                                                    } else {
                                                        handleAnswerChange(index, 'estCorrecte', e.target.checked);
                                                    }
                                                }}
                                            />
                                            {t('Vrai')}
                                        </label>
                                        {!isYesNo && (
                                            <button
                                                type="button"
                                                className="qz-icon-btn qz-icon-btn--danger"
                                                onClick={() => removeAnswerRow(index)}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {type === 'MATCHING' && (
                            <div className="qz-section">
                                <div className="qz-section-head">
                                    <h4>{t('Paires de correspondance')}</h4>
                                    <button type="button" className="qz-btn qz-btn-light" onClick={addMatchingPair}>
                                        <Plus size={14} /> {t('Ajouter')}
                                    </button>
                                </div>

                                {matchingPairs.map((pair, index) => (
                                    <div key={index} className="qz-skill-row" style={{ gridTemplateColumns: '1fr 1fr 40px' }}>
                                        <input
                                            placeholder={t('Gauche')}
                                            value={pair.left}
                                            onChange={(e) => handleMatchingPairChange(index, 'left', e.target.value)}
                                        />
                                        <input
                                            placeholder={t('Droite')}
                                            value={pair.right}
                                            onChange={(e) => handleMatchingPairChange(index, 'right', e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="qz-icon-btn qz-icon-btn--danger"
                                            onClick={() => removeMatchingPair(index)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {type === 'CLASSEMENT' && (
                            <div className="qz-section">
                                <div className="qz-section-head">
                                    <h4>{t('Éléments à classer')}</h4>
                                    <button type="button" className="qz-btn qz-btn-light" onClick={addAnswerRow}>
                                        <Plus size={14} /> {t('Ajouter')}
                                    </button>
                                </div>

                                {formData.reponsesPossibles.map((answer, index) => (
                                    <div key={index} className="qz-skill-row" style={{ gridTemplateColumns: '1fr 80px 40px' }}>
                                        <input
                                            placeholder={t('Libellé')}
                                            value={answer.libelle}
                                            onChange={(e) => handleAnswerChange(index, 'libelle', e.target.value)}
                                        />
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder={t('Ordre')}
                                            value={answer.ordreCorrect}
                                            onChange={(e) => handleAnswerChange(index, 'ordreCorrect', e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="qz-icon-btn qz-icon-btn--danger"
                                            onClick={() => removeAnswerRow(index)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {type === 'DISSERTATION' && (
                            <div className="qz-section">
                                <div className="qz-section-head">
                                    <h4>{t('Configuration dissertation')}</h4>
                                </div>
                                <div className="qz-grid" style={{ marginBottom: 15 }}>
                                    <div className="qz-field">
                                        <label>{t('Nb mots min')}</label>
                                        <input
                                            type="number"
                                            value={dissertationConfig.nbMotsMin}
                                            onChange={(e) => setDissertationConfig(p => ({ ...p, nbMotsMin: e.target.value }))}
                                        />
                                    </div>
                                    <div className="qz-field">
                                        <label>{t('Nb mots max')}</label>
                                        <input
                                            type="number"
                                            value={dissertationConfig.nbMotsMax}
                                            onChange={(e) => setDissertationConfig(p => ({ ...p, nbMotsMax: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="qz-section-head" style={{ marginTop: 10 }}>
                                    <h4>{t('Grille de correction')}</h4>
                                    <button type="button" className="qz-btn qz-btn-light" onClick={addGrilleItem}>
                                        <Plus size={14} /> {t('Ajouter critère')}
                                    </button>
                                </div>
                                {dissertationConfig.grille.map((item, index) => (
                                    <div key={index} className="qz-skill-row" style={{ gridTemplateColumns: '80px 1fr 80px 40px' }}>
                                        <input
                                            placeholder="ID"
                                            value={item.id}
                                            onChange={(e) => handleGrilleChange(index, 'id', e.target.value)}
                                        />
                                        <input
                                            placeholder={t('Critère')}
                                            value={item.label}
                                            onChange={(e) => handleGrilleChange(index, 'label', e.target.value)}
                                        />
                                        <input
                                            type="number"
                                            placeholder="Max"
                                            value={item.max}
                                            onChange={(e) => handleGrilleChange(index, 'max', e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="qz-icon-btn qz-icon-btn--danger"
                                            onClick={() => removeGrilleItem(index)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {type === 'GAMING' && (
                            <div className="qz-section">
                                <div className="qz-section-head">
                                    <h4>{t('Configuration Gaming')}</h4>
                                </div>
                                <div className="qz-grid">
                                    <div className="qz-field">
                                        <label>{t('Timer (sec)')}</label>
                                        <input
                                            type="number"
                                            value={gamingConfig.timer_s}
                                            onChange={(e) => setGamingConfig(p => ({ ...p, timer_s: e.target.value }))}
                                        />
                                    </div>
                                    <div className="qz-field">
                                        <label>{t('Animation')}</label>
                                        <select value={gamingConfig.animation} onChange={(e) => setGamingConfig(p => ({ ...p, animation: e.target.value }))}>
                                            {ANIMATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="qz-section-head" style={{ marginTop: 15 }}>
                                    <h4>{t('Réponses')}</h4>
                                    <button type="button" className="qz-btn qz-btn-light" onClick={addAnswerRow}>
                                        <Plus size={14} /> {t('Ajouter')}
                                    </button>
                                </div>
                                {formData.reponsesPossibles.map((answer, index) => (
                                    <div key={index} className="qz-skill-row" style={{ gridTemplateColumns: '1fr auto 40px' }}>
                                        <input
                                            placeholder={t('Libellé')}
                                            value={answer.libelle}
                                            onChange={(e) => handleAnswerChange(index, 'libelle', e.target.value)}
                                        />
                                        <input
                                            type="radio"
                                            name="gaming-correct"
                                            checked={!!answer.estCorrecte}
                                            onChange={() => setFormData(p => ({
                                                ...p,
                                                reponsesPossibles: p.reponsesPossibles.map((rp, i) => ({ ...rp, estCorrecte: i === index }))
                                            }))}
                                        />
                                        <button type="button" className="qz-icon-btn qz-icon-btn--danger" onClick={() => removeAnswerRow(index)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {type === 'COMPLETEMENT' && (
                            <div className="qz-section">
                                <div className="qz-section-head">
                                    <h4>{t('Réponse attendue')}</h4>
                                </div>
                                <div className="qz-field">
                                    <input
                                        placeholder={t('La bonne réponse à saisir')}
                                        value={completionConfig.expected}
                                        onChange={(e) => setCompletionConfig({ expected: e.target.value })}
                                    />
                                    <div className="qz-config-box">
                                        {t('L\'utilisateur devra saisir ce texte exactement.')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                <div className="qz-modal-footer">
                    <button className="qz-btn qz-btn-light" onClick={onClose} disabled={loading}>{t('Annuler')}</button>
                    <button className="qz-btn qz-btn-primary" type="submit" form="question-form" disabled={loading}>
                        {loading ? t('Chargement...') : (editingQuestion ? t('Mettre à jour') : t('Créer'))}
                    </button>
                </div>
            </div>
        </div>
    );
};

const QuestionManagement = ({
    currentQuiz,
    questions,
    domaines = [],
    themes = [],
    loading,
    message,
    error,
    getQuiz,
    getQuestionsByQuiz,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    getAllDomaines,
    getAllThemes,
    clearMessage
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const quizId = params.quizId ? Number(params.quizId) : null;
    const quizBasePath = location.state?.quizBasePath
        || (location.pathname.includes('/formation/quizzes') ? '/formation/quizzes' : '')
        || (location.pathname.includes('/skills/quizzes') ? '/skills/quizzes' : '')
        || (location.pathname.includes('/recrutement/onboarding/quizzes') ? '/recrutement/onboarding/quizzes' : '')
        || '/quizzes';

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (quizId) {
            getQuiz(quizId);
            getQuestionsByQuiz(quizId);
        }
        getAllDomaines();
        getAllThemes();
        return () => clearMessage();
    }, [quizId, getQuiz, getQuestionsByQuiz, getAllDomaines, getAllThemes, clearMessage]);

    const quiz = location.state?.quiz || currentQuiz;

    const orderedQuestions = useMemo(() => {
        return [...(questions || [])].sort((a, b) => {
            const orderA = Number(a.ordre || 0);
            const orderB = Number(b.ordre || 0);
            if (orderA !== orderB) return orderA - orderB;
            return Number(a.id || 0) - Number(b.id || 0);
        });
    }, [questions]);

    const filteredQuestions = useMemo(() => {
        return orderedQuestions.filter((question) => {
            const text = searchTerm.toLowerCase();
            return (
                (question.enonce || '').toLowerCase().includes(text) ||
                (question.typeQuestion || '').toLowerCase().includes(text) ||
                (question.domaineNom || '').toLowerCase().includes(text) ||
                (question.themeNom || '').toLowerCase().includes(text)
            );
        });
    }, [orderedQuestions, searchTerm]);

    const nextQuestionOrder = useMemo(() => {
        const maxOrder = orderedQuestions.reduce((max, question) => {
            const order = Number(question.ordre || 0);
            return Number.isFinite(order) ? Math.max(max, order) : max;
        }, 0);
        return Math.max(maxOrder, orderedQuestions.length) + 1;
    }, [orderedQuestions]);

    const handleSubmit = (payload) => {
        if (editingQuestion) {
            updateQuestion(editingQuestion.id, payload);
        } else {
            createQuestion(payload);
        }
        setEditingQuestion(null);
        setIsModalOpen(false);
    };

    const handleDelete = (question) => {
        Swal.fire({
            title: t('Êtes-vous sûr ?'),
            text: t('Voulez-vous vraiment supprimer cette question ?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('Oui, supprimer'),
            cancelButtonText: t('Annuler')
        }).then((result) => {
            if (result.isConfirmed) {
                deleteQuestion(question.id);
            }
        });
    };

    return (
        <div className="qz-page">
            <div className="qz-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
                        {t('Quiz')} <span style={{ opacity: 0.5 }}>/</span> {quiz?.titre || `#${quizId}`} <span style={{ opacity: 0.5 }}>/</span> {t('Questions')}
                    </div>
                    <h2>{t('Gestion des Questions')}</h2>
                    {/*<p>{t('Personnalisez les questions et les réponses pour ce quiz')}</p>*/}
                </div>

                <div className="qz-actions">
                    <button className="qz-btn qz-btn-light" onClick={() => navigate(quizBasePath)}>
                        <ArrowLeft size={14} /> {t('Retour')}
                    </button>
                    <button className="qz-btn qz-btn-primary" onClick={() => {
                        setEditingQuestion(null);
                        setIsModalOpen(true);
                    }}>
                        <Plus size={15} /> {t('Ajouter une question')}
                    </button>
                </div>
            </div>

            <div className="qz-toolbar">
                <div className="qz-search-wrap">
                    <Search size={14} className="qz-search-icon" />
                    <input
                        type="text"
                        className="qz-search"
                        placeholder={t('Rechercher une question...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "#6b7280" }}>
                    {loading ? "..." : `${filteredQuestions.length} question(s)`}
                </div>
            </div>

            {message && <div style={{ margin: '14px 18px', padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>{message}</div>}
            {error && <div style={{ margin: '14px 18px', padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>{error}</div>}

            <div className="qz-card">
                <div className="qz-table-wrap">
                    <table className="qz-table">
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>{t('Ordre')}</th>
                                <th>{t('Énoncé')}</th>
                                <th>{t('Type')}</th>
                                <th style={{ width: 80 }}>{t('Points')}</th>
                                <th>{t('Domaine / Thème')}</th>
                                <th style={{ width: 80 }}>{t('Réponses')}</th>
                                <th style={{ textAlign: "right", width: 100 }}>{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: 32 }}>{t('Chargement...')}</td>
                                </tr>
                            ) : filteredQuestions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: "center", padding: 32, opacity: 0.6 }}>{t('Aucune question trouvée')}</td>
                                </tr>
                            ) : (
                                filteredQuestions.map((question, index) => (
                                    <tr key={question.id}>
                                        <td style={{ fontWeight: 800, color: '#2f4595' }}>#{index + 1}</td>
                                        <td>
                                            <div style={{ fontWeight: 700, color: "#2f4595" }}>{question.enonce}</div>
                                            {question.explicationCorrection && (
                                                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
                                                    <Info size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                                    {question.explicationCorrection}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <span className="qz-badge qz-badge--blue">{question.typeQuestion}</span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 700 }}>{question.points}</span>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 11, color: '#374151' }}>
                                                {question.domaineNom || '—'}
                                                <span style={{ margin: '0 4px', opacity: 0.4 }}>/</span>
                                                {question.themeNom || '—'}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="qz-badge qz-badge--grey">
                                                {question.reponsesPossibles?.length || 0}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div className="qz-actions">
                                                <button
                                                    className="qz-icon-btn"
                                                    title={t('Modifier')}
                                                    onClick={() => {
                                                        setEditingQuestion(question);
                                                        setIsModalOpen(true);
                                                    }}
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="qz-icon-btn qz-icon-btn--danger"
                                                    title={t('Supprimer')}
                                                    onClick={() => handleDelete(question)}
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <QuestionModal
                isOpen={isModalOpen}
                onClose={() => {
                    setEditingQuestion(null);
                    setIsModalOpen(false);
                }}
                onSubmit={handleSubmit}
                editingQuestion={editingQuestion}
                loading={loading}
                quizId={quizId}
                domaines={domaines}
                themes={themes}
                nextOrder={nextQuestionOrder}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    currentQuiz: state.quizzes?.currentQuiz,
    questions: state.quizzes?.questions || [],
    loading: state.quizzes?.loading,
    message: state.quizzes?.message,
    error: state.quizzes?.error,
    domaines: state.paramDomainesThemes?.domaines || state.domaines?.domaines || [],
    themes: state.paramDomainesThemes?.themes || state.themes?.themes || []
});

const mapDispatchToProps = (dispatch) => ({
    getQuiz: (quizId) => dispatch(getQuizRequest(quizId)),
    getQuestionsByQuiz: (quizId) => dispatch(getQuestionsByQuizRequest(quizId)),
    createQuestion: (payload) => dispatch(createQuestionRequest(payload)),
    updateQuestion: (questionId, payload) => dispatch(updateQuestionRequest(questionId, payload)),
    deleteQuestion: (questionId) => dispatch(deleteQuestionRequest(questionId)),
    getAllDomaines: () => dispatch(fetchDomainesRequest()),
    getAllThemes: () => dispatch(fetchThemesRequest()),
    clearMessage: () => dispatch(clearQuizMessage())
});

export default connect(mapStateToProps, mapDispatchToProps)(QuestionManagement);
